import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Settings2, Info } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useAuth } from '../hooks/useAuth'
import { useSimpleToast } from '../hooks/useToast'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Skeleton } from '../components/ui/skeleton'
import { PlanMatrixTable } from '../components/training-plans/PlanMatrixTable'
import { PlanRadarChart } from '../components/training-plans/PlanRadarChart'
import { ExercisePickerDialog } from '../components/training-plans/ExercisePickerDialog'
import { WeekDialog } from '../components/training-plans/WeekDialog'
import { SavePlanConfirmDialog } from '../components/training-plans/SavePlanConfirmDialog'
import { PlanDetailsDialog } from '../components/training-plans/PlanDetailsDialog'
import { planWeekFor, statusBadgeVariant } from '../components/training-plans/plan-helpers'
import {
  planToMatrix,
  frequencySnapshot,
  computeAffectedWeeks,
  matrixToPayload,
  cellKey,
  type MatrixState,
  type MatrixWeek,
} from '../components/training-plans/matrix-helpers'
import { trainingPlansService } from '../services/training-plans'
import type { Exercise } from '../types/exercise'
import type { TrainingPlan, TrainingPlanWeekKind } from '../types/training-plan'

export default function TrainingPlanBuilderPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { showSuccess, showError } = useSimpleToast()

  const [plan, setPlan] = useState<TrainingPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [matrix, setMatrix] = useState<MatrixState>({ rows: [], weeks: [], frequencies: {} })
  const [initialFreq, setInitialFreq] = useState<Record<string, number>>({})

  const [pickerOpen, setPickerOpen] = useState(false)
  const [weekDialog, setWeekDialog] = useState<MatrixWeek | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [affectedWeeks, setAffectedWeeks] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  const hydrate = useCallback((loaded: TrainingPlan) => {
    setPlan(loaded)
    const next = planToMatrix(loaded)
    setMatrix(next)
    setInitialFreq(frequencySnapshot(next))
  }, [])

  useEffect(() => {
    if (!id) return
    setLoading(true)
    trainingPlansService
      .get(id)
      .then(hydrate)
      .catch(() => showError(t('trainingPlans.builder.loadError')))
      .finally(() => setLoading(false))
  }, [id, hydrate, showError, t])

  const planTitle = plan?.goal?.trim() || (plan ? t(`trainingPlans.discipline.${plan.discipline ?? 'bouldering'}`) : '')

  useBreadcrumb(
    () => ({
      onBack: () => navigate('/training-plans'),
      segments: [
        { label: t('trainingPlans.pageTitle'), onClick: () => navigate('/training-plans') },
        { label: planTitle || t('trainingPlans.builder.title') },
      ],
    }),
    [t, navigate, planTitle]
  )

  function handleFrequencyChange(weekNumber: number, exerciseId: number, value: number) {
    setMatrix((prev) => ({
      ...prev,
      frequencies: { ...prev.frequencies, [cellKey(weekNumber, exerciseId)]: value },
    }))
  }

  function handleRemoveExercise(exerciseId: number) {
    setMatrix((prev) => {
      const frequencies = { ...prev.frequencies }
      for (const week of prev.weeks) delete frequencies[cellKey(week.week_number, exerciseId)]
      return {
        ...prev,
        rows: prev.rows.filter((r) => r.exercise_id !== exerciseId),
        frequencies,
      }
    })
  }

  function handleAddExercises(exercises: Exercise[]) {
    setMatrix((prev) => {
      const existing = new Set(prev.rows.map((r) => r.exercise_id))
      const additions = exercises
        .filter((ex) => !existing.has(Number(ex.id)))
        .map((ex, i) => ({
          exercise_id: Number(ex.id),
          exercise: ex,
          position: prev.rows.length + i,
        }))
      return { ...prev, rows: [...prev.rows, ...additions] }
    })
  }

  function handleWeekSave(weekNumber: number, kind: TrainingPlanWeekKind, description: string | null) {
    setMatrix((prev) => ({
      ...prev,
      weeks: prev.weeks.map((w) => (w.week_number === weekNumber ? { ...w, kind, description } : w)),
    }))
  }

  function handleSaveClick() {
    const weeks = plan?.status === 'active' ? computeAffectedWeeks(initialFreq, matrix) : []
    setAffectedWeeks(weeks)
    setConfirmOpen(true)
  }

  async function handleConfirmSave() {
    if (!id || !plan) return
    setSaving(true)
    try {
      const parts = matrixToPayload(matrix, initialFreq)
      const updated = await trainingPlansService.update(id, {
        ...parts,
        apply_week_numbers: plan.status === 'active' ? affectedWeeks : [],
      })
      hydrate(updated)
      showSuccess(t('trainingPlans.builder.saved'))
      setConfirmOpen(false)
    } catch {
      showError(t('trainingPlans.builder.saveError'))
    } finally {
      setSaving(false)
    }
  }

  if (loading || !plan) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    )
  }

  const currentUserId = user ? String(user.id) : ''
  // A coach-managed plan is editable only by the coach; otherwise by the athlete.
  const canManage = plan.coach ? plan.coach.id === currentUserId : plan.user.id === currentUserId

  return (
    // No width cap: the weeks × exercises grid gets the whole screen (see the
    // full-bleed <main> in App.tsx for this route).
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-xl font-semibold truncate">{planTitle}</h1>
          <Badge variant={statusBadgeVariant(plan.status)}>{t(`trainingPlans.status.${plan.status}`)}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setDetailsOpen(true)}>
            <Info className="size-4" />
            <span className="hidden sm:inline">{t('trainingPlans.builder.viewDetails')}</span>
          </Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate(`/training-plans/${id}/details`)}>
                <Settings2 className="size-4" />
                <span className="hidden sm:inline">{t('trainingPlans.builder.editDetails')}</span>
              </Button>
              <Button size="sm" onClick={handleSaveClick}>{t('trainingPlans.builder.save')}</Button>
            </>
          )}
        </div>
      </div>

      {/* The week header (with base/rest labels) stays visible even before any
          exercise is added, so the plan structure is always readable. */}
      <PlanMatrixTable
        matrix={matrix}
        readOnly={!canManage}
        currentWeekNumber={planWeekFor(plan, new Date())}
        onFrequencyChange={handleFrequencyChange}
        onWeekClick={(weekNumber) =>
          setWeekDialog(matrix.weeks.find((w) => w.week_number === weekNumber) ?? null)
        }
        onRemoveExercise={handleRemoveExercise}
      />

      {matrix.rows.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('trainingPlans.builder.noExercises')}</p>
        </div>
      )}

      <PlanRadarChart matrix={matrix} />

      {canManage && (
        <Button
          size="icon"
          onClick={() => setPickerOpen(true)}
          aria-label={t('trainingPlans.builder.addExercise')}
          className="fixed right-4 bottom-20 md:bottom-6 z-40 h-14 w-14 rounded-full shadow-lg"
        >
          <Plus className="size-6" />
        </Button>
      )}

      <ExercisePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        excludeIds={matrix.rows.map((r) => String(r.exercise_id))}
        onConfirm={handleAddExercises}
      />

      <WeekDialog week={weekDialog} onOpenChange={(open) => !open && setWeekDialog(null)} onSave={handleWeekSave} />

      <SavePlanConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        affectedWeeks={affectedWeeks}
        saving={saving}
        onConfirm={handleConfirmSave}
      />

      <PlanDetailsDialog plan={plan} open={detailsOpen} onOpenChange={setDetailsOpen} />
    </div>
  )
}
